import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import { alpha } from "@mui/material/styles";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Validation
import validateEmployee from "../schemas/validations";

function EmployeeForm({ 
  formData, 
  handleChange, 
  departments, 
  positions, 
  managers, 
  degrees, 
  contractTypes, 
  natureOfWorkOptions,
  loadingStates = {
    departments: false,
    positions: false,
    managers: false,
    degrees: false,
    contractTypes: false,
    natureOfWorkOptions: false
  },
  errorStates = {
    departments: null,
    positions: null,
    managers: null,
    degrees: null,
    contractTypes: null,
    natureOfWorkOptions: null
  }
}) {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [managerInputValue, setManagerInputValue] = useState('');

  const handleInputChange = (field, value) => {
    handleChange(field, value);
    if (touched[field]) {
      const validationErrors = validateEmployee({
        ...formData,
        [field]: value
      });
      setErrors(prev => ({
        ...prev,
        [field]: validationErrors[field]
      }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const validationErrors = validateEmployee({
      ...formData
    });
    setErrors(prev => ({
      ...prev,
      [field]: validationErrors[field]
    }));
  };

  useEffect(()=>{
    console.log('loadingStates', loadingStates)
  }, [loadingStates])

  const validateForm = () => {
    const validationErrors = validateEmployee(formData);
    setErrors(validationErrors);
    setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
    return Object.keys(validationErrors).length === 0;
  };

  // Helper function to render loading or error state for dropdowns
  const renderDropdownState = (field) => {
    if (loadingStates[field]) {
      return (
        <MenuItem disabled>
          <MDBox display="flex" alignItems="center" justifyContent="center" width="100%">
            <CircularProgress size={20} />
            <MDTypography variant="caption" ml={1}>Loading...</MDTypography>
          </MDBox>
        </MenuItem>
      );
    }
    
    if (errorStates[field]) {
      return (
        <MenuItem disabled>
          <MDTypography color="error" variant="caption">
            Failed to load: {errorStates[field]}
          </MDTypography>
        </MenuItem>
      );
    }
    
    return null;
  };

  // الحصول على اسم المدير المحدد لعرضه في AutoComplete
  const getSelectedManager = () => {
    if (!formData.managerId) return null;
    const selectedManager = managers.find(m => m.id === formData.managerId);
    return selectedManager || null;
  };

  return (
    <MDBox>
      <MDBox display="flex" alignItems="center" mb={3}>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="3.5rem"
          height="3.5rem"
          variant="gradient"
          bgColor="info"
          color="white"
          shadow="md"
          borderRadius="xl"
          mr={2}
        >
          <PersonAddIcon sx={{ fontSize: 28 }} />
        </MDBox>
        <MDBox>
          <MDTypography variant="h3" fontWeight="medium" color="info" mb={0.5}>
            إضافة موظف جديد / New Employee
          </MDTypography>
          <MDTypography variant="body2" color="text" opacity={0.8}>
            أدخل بيانات الموظف الجديد / Enter new employee details
          </MDTypography>
        </MDBox>
      </MDBox>
{!loadingStates.managers ?
  managers.length > 0 || departments.length > 0 ?
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* First Row - unchanged */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <MDInput
              type="text"
              label="اسم الموظف / Employee Name *"
              value={formData.name || ''}
              onChange={(e) => handleInputChange("name", e.target.value)}
              onBlur={() => handleBlur("name")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                  "& fieldset": {
                    borderColor: touched.name && !formData.name ? '#d32f2f' : undefined,
                  }
                },
              }}
            />
            {touched.name && errors.name && (
              <FormHelperText error>{errors.name}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <MDInput
              type="email"
              label="البريد الإلكتروني / Email *"
              value={formData.email || ''}
              onChange={(e) => handleInputChange("email", e.target.value)}
              onBlur={() => handleBlur("email")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                  "& fieldset": {
                    borderColor: touched.email && !formData.email ? '#d32f2f' : undefined,
                  }
                },
              }}
            />
            {touched.email && errors.email && (
              <FormHelperText error>{errors.email}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.phone}>
            <MDInput
              type="tel"
              label="رقم الهاتف / Phone Number *"
              value={formData.phone || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                handleInputChange("phone", value);
              }}
              onBlur={() => handleBlur("phone")}
              inputProps={{
                maxLength: 11, 
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                  "& fieldset": {
                    borderColor: touched.phone && errors.phone ? '#d32f2f' : undefined,
                  }
                },
              }}
            />
            {touched.phone && errors.phone && (
              <FormHelperText error>
                {errors.phone? "يجب أن يكون رقم الهاتف 11 رقمًا على الأقل" 
                  : "رقم الهاتف مطلوب"}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Second Row */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errorStates.departments || (touched.departmentId && !formData.departmentId)}>
            <InputLabel>القسم / Department *</InputLabel>
            <Select
              value={formData.departmentId}
              onChange={(e) => handleInputChange("departmentId", e.target.value)}
              onBlur={() => handleBlur("departmentId")}
              label="القسم / Department"
              sx={{
                height: "42px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.palette.info.main,
                },
                "& fieldset": {
                  borderColor: touched.departmentId && !formData.departmentId ? '#d32f2f' : undefined,
                }
              }}
            >
              <MenuItem value="">
                <em>اختر القسم / Select Department</em>
              </MenuItem>
              {renderDropdownState('departments')}
              {!loadingStates.departments && !errorStates.departments && departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
            {errorStates.departments && (
              <FormHelperText error>{errorStates.departments}</FormHelperText>
            )}
            {touched.departmentId && !formData.departmentId && (
              <FormHelperText error>القسم مطلوب / Department is required</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errorStates.positions || (touched.positionId && !formData.positionId)}>
            <InputLabel>المسمى الوظيفي / Job Title *</InputLabel>
            <Select
              value={formData.positionId}
              onChange={(e) => handleInputChange("positionId", e.target.value)}
              onBlur={() => handleBlur("positionId")}
              label="المسمى الوظيفي / Job Title"
              sx={{
                height: "42px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.palette.info.main,
                },
                "& fieldset": {
                  borderColor: touched.positionId && !formData.positionId ? '#d32f2f' : undefined,
                }
              }}
            >
              <MenuItem value="">
                <em>اختر المنصب / Select Position</em>
              </MenuItem>
              {renderDropdownState('positions')}
              {!loadingStates.positions && !errorStates.positions && positions.map((pos) => (
                <MenuItem key={pos.id} value={pos.id}>
                  {pos.name}
                </MenuItem>
              ))}
            </Select>
            {errorStates.positions && (
              <FormHelperText error>{errorStates.positions}</FormHelperText>
            )}
            {touched.positionId && !formData.positionId && (
              <FormHelperText error>المسمى الوظيفي مطلوب / Job title is required</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <MDInput
              type="date"
              label="تاريخ الميلاد / Date of Birth *"
              value={formData.dob || ''}
              onChange={(e) => handleInputChange("dob", e.target.value)}
              onBlur={() => handleBlur("dob")}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                  "& fieldset": {
                    borderColor: touched.dob && !formData.dob ? '#d32f2f' : undefined,
                  }
                },
              }}
            />
          </FormControl>
        </Grid>

        {/* Third Row */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <MDInput
              type="date"
              label="تاريخ الإلتحاق / Join Date *"
              value={formData.joinDate || ''}
              onChange={(e) => handleInputChange("joinDate", e.target.value)}
              onBlur={() => handleBlur("joinDate")}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                  "& fieldset": {
                    borderColor: touched.joinDate && !formData.joinDate ? '#d32f2f' : undefined,
                  }
                },
              }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.natureOfWork || !!errorStates.natureOfWorkOptions}>
            <InputLabel>طبيعة العمل / Nature of Work *</InputLabel>
            <Select
              value={formData.natureOfWork || 0}
              onChange={(e) => handleInputChange("natureOfWork", Number(e.target.value))}
              onBlur={() => handleBlur("natureOfWork")}
              label="طبيعة العمل / Nature of Work"
              sx={{
                height: "42px",
                "&:hover fieldset": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
                "& fieldset": {
                  borderColor: touched.natureOfWork && errors.natureOfWork ? '#d32f2f' : undefined,
                }
              }}
            >
              <MenuItem value={0}>
                <em>اختر طبيعة العمل / Select Nature of Work</em>
              </MenuItem>
              {renderDropdownState('natureOfWorkOptions')}
              {!loadingStates.natureOfWorkOptions && !errorStates.natureOfWorkOptions && natureOfWorkOptions.map((option) => (
                <MenuItem key={option.id} value={Number(option.id)}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
            {errorStates.natureOfWorkOptions && (
              <FormHelperText error>{errorStates.natureOfWorkOptions}</FormHelperText>
            )}
            {errors.natureOfWork && (
              <FormHelperText error>{errors.natureOfWork}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errorStates.contractTypes || (touched.contractType && !formData.contractType)}>
            <InputLabel>نوع العقد / Contract Type *</InputLabel>
            <Select
              value={formData.contractType || ""}
              onChange={(e) => handleInputChange("contractType", e.target.value)}
              onBlur={() => handleBlur("contractType")}
              label="نوع العقد / Contract Type"
              sx={{
                height: "42px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.palette.info.main,
                },
                "& fieldset": {
                  borderColor: touched.contractType && !formData.contractType ? '#d32f2f' : undefined,
                }
              }}
            >
              <MenuItem value="">
                <em>اختر نوع العقد / Select Contract Type</em>
              </MenuItem>
              {renderDropdownState('contractTypes')}
              {!loadingStates.contractTypes && !errorStates.contractTypes && contractTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
            {errorStates.contractTypes && (
              <FormHelperText error>{errorStates.contractTypes}</FormHelperText>
            )}
            {touched.contractType && !formData.contractType && (
              <FormHelperText error>نوع العقد مطلوب / Contract type is required</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Fourth Row */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errorStates.degrees || (touched.degree && !formData.degree)}>
            <InputLabel>الدرجة العلمية / Degree *</InputLabel>
            <Select
              value={formData.degree}
              onChange={(e) => handleInputChange("degree", e.target.value)}
              onBlur={() => handleBlur("degree")}
              label="الدرجة العلمية / Degree"
              sx={{
                height: "42px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.palette.info.main,
                },
                "& fieldset": {
                  borderColor: touched.degree && !formData.degree ? '#d32f2f' : undefined,
                }
              }}
            >
              <MenuItem value="">
                <em>اختر الدرجة العلمية / Select Degree</em>
              </MenuItem>
              {renderDropdownState('degrees')}
              {!loadingStates.degrees && !errorStates.degrees && degrees.map((degree) => (
                <MenuItem key={degree.id} value={degree.id}>
                  {degree.name}
                </MenuItem>
              ))}
            </Select>
            {errorStates.degrees && (
              <FormHelperText error>{errorStates.degrees}</FormHelperText>
            )}
            {touched.degree && !formData.degree && (
              <FormHelperText error>الدرجة العلمية مطلوبة / Degree is required</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <MDInput
              type="text"
              label="التخصص / Major *"
              value={formData.major || ''}
              onChange={(e) => handleInputChange("major", e.target.value)}
              onBlur={() => handleBlur("major")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                  "& fieldset": {
                    borderColor: touched.major && !formData.major ? '#d32f2f' : undefined,
                  }
                },
              }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <MDInput
              type="text"
              label="المنصب / Position *"
              value={formData.jobTitle || ''}
              onChange={(e) => handleInputChange("jobTitle", e.target.value)}
              onBlur={() => handleBlur("jobTitle")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                  "& fieldset": {
                    borderColor: touched.jobTitle && !formData.jobTitle ? '#d32f2f' : undefined,
                  }
                },
              }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
        <FormControl fullWidth error={!!errors.managerId || !!errorStates.managers}>
          <Autocomplete
            options={managers} 
            getOptionLabel={(option) => option.name}
            value={getSelectedManager()}
            onChange={(event, newValue) => {
              handleInputChange("managerId", newValue ? newValue.id : 0);
            }}
            inputValue={managerInputValue}
            onInputChange={(event, newInputValue) => {
              setManagerInputValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="المدير / Manager *"
                error={!!errors.managerId}
                helperText={errors.managerId}
                onBlur={() => handleBlur("managerId")}
              />
            )}
            loading={loadingStates.managers}
            loadingText="جاري التحميل..."
            noOptionsText="لا توجد نتائج"
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
                "& fieldset": {
                  borderColor: touched.managerId && errors.managerId ? '#d32f2f' : undefined,
                }
              },
            }}
          />
          {errorStates.managers && (
            <FormHelperText error>{errorStates.managers}</FormHelperText>
          )}
        </FormControl>
      </Grid>

        {/* Fifth Row */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.badgeNumber}>
            <MDInput
              type="text"
              label="رقم الشارة / Badge Number *"
              value={formData.badgeNumber || ''}
              onChange={(e) => handleInputChange("badgeNumber", e.target.value)}
              onBlur={() => handleBlur("badgeNumber")}
              error={!!errors.badgeNumber}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                  "& fieldset": {
                    borderColor: touched.badgeNumber && errors.badgeNumber ? '#d32f2f' : undefined,
                  }
                },
              }}
            />
            {errors.badgeNumber && (
              <FormHelperText error>{errors.badgeNumber}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.fingerPrintId}>
            <MDInput
              type="number"
              label="رقم البصمة / Fingerprint ID *"
              value={formData.fingerPrintId || 0}
              onChange={(e) => handleInputChange("fingerPrintId", parseInt(e.target.value) || 0)}
              onBlur={() => handleBlur("fingerPrintId")}
              error={!!errors.fingerPrintId}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                  "& fieldset": {
                    borderColor: touched.fingerPrintId && errors.fingerPrintId ? '#d32f2f' : undefined,
                  }
                },
              }}
            />
            {errors.fingerPrintId && (
              <FormHelperText error>{errors.fingerPrintId}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>الجنس / Gender *</InputLabel>
            <Select
              value={formData.gender}
              onChange={(e) => handleInputChange("gender", e.target.value)}
              onBlur={() => handleBlur("gender")}
              label="الجنس / Gender"
              sx={{
                height: "42px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.palette.info.main,
                },
                "& fieldset": {
                  borderColor: touched.gender && !formData.gender ? '#d32f2f' : undefined,
                }
              }}
            >
              <MenuItem value="">
                <em>اختر الجنس / Select Gender</em>
              </MenuItem>
              <MenuItem value={true}>ذكر / Male</MenuItem>
              <MenuItem value={false}>أنثى / Female</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>حالة الموظف / Employee Status *</InputLabel>
            <Select
              value={formData.isResigned}
              onChange={(e) => handleInputChange("isResigned", e.target.value)}
              onBlur={() => handleBlur("isResigned")}
              label="حالة الموظف / Employee Status"
              sx={{
                height: "42px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.palette.info.main,
                },
                "& fieldset": {
                  borderColor:touched.isRequired &&  !formData.isResigned ? '#d32f2f' : undefined,
                }
              }}
            >
              <MenuItem value="">
                <em>اختر الحالة / Select Status</em>
              </MenuItem>
              <MenuItem value={false}>مستمر بالخدمة / Active</MenuItem>
              <MenuItem value={true}>مستقيل / Resigned</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      :
      <MDBox container>
        <MDBox display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
          <MDTypography variant="h6" color="text" textAlign="center">
            لا توجد بيانات متاحة يرجى المحاولة مرة اخرى!.
          </MDTypography>
          </MDBox>
          </MDBox>
          :
          <MDBox container>
        <MDBox display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
          <MDTypography variant="h6" color="text" textAlign="center">
            جار التحميل...
          </MDTypography>
          </MDBox>
          </MDBox>
}
    </MDBox>
  );
}

EmployeeForm.propTypes = {
  formData: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    positionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    managerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    degree: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dob: PropTypes.string,
    joinDate: PropTypes.string,
    natureOfWork: PropTypes.oneOfType([PropTypes.number]),
    contractType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    major: PropTypes.string,
    jobTitle: PropTypes.string,
    badgeNumber: PropTypes.string,
    fingerPrintId: PropTypes.number,
    gender: PropTypes.bool,
    isResigned: PropTypes.bool
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  departments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  positions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  managers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  degrees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  natureOfWorkOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  contractTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  loadingStates: PropTypes.shape({
    departments: PropTypes.bool,
    positions: PropTypes.bool,
    managers: PropTypes.bool,
    degrees: PropTypes.bool,
    contractTypes: PropTypes.bool,
    natureOfWorkOptions: PropTypes.bool
  }),
  errorStates: PropTypes.shape({
    departments: PropTypes.string,
    positions: PropTypes.string,
    managers: PropTypes.string,
    degrees: PropTypes.string,
    contractTypes: PropTypes.string,
    natureOfWorkOptions: PropTypes.string
  })
};

EmployeeForm.defaultProps = {
  loadingStates: {
    departments: false,
    positions: false,
    managers: false,
    degrees: false,
    contractTypes: false,
    natureOfWorkOptions: false
  },
  errorStates: {
    departments: null,
    positions: null,
    managers: null,
    degrees: null,
    contractTypes: null,
    natureOfWorkOptions: null
  }
};

export default EmployeeForm;